import React from "react";
import {
  TagIcon,
  BriefcaseIcon,
  CalendarIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

/* ---------- Helpers ---------- */
const formatDate = (d) => {
  if (!d) return null;
  if (d.seconds) return new Date(d.seconds * 1000).toLocaleDateString();
  if (d._seconds) return new Date(d._seconds * 1000).toLocaleDateString();
  if (typeof d === "number") return new Date(d).toLocaleDateString();
  if (typeof d === "string") {
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) return dt.toLocaleDateString();
  }
  return null;
};

const Pill = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-200/80 bg-white px-2.5 py-1.5 shadow-sm text-sm">
      <Icon className="h-4 w-4 text-gray-600" />
      <span className="text-gray-700 truncate">{value}</span>
    </div>
  );
};

/* ---------- Project Preview Card ---------- */
const ProjectPreviewCard = ({ project = {}, editable = false, onEdit }) => {
  const { title, oneLiner, description, image, category, tags = [], openRoles = [] } = project;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm ring-1 ring-black/5 overflow-hidden">
      {/* Image */}
      <div className="h-40 w-full bg-neutral-100 overflow-hidden relative">
        {image ? (
          <img src={image} alt={title || "Preview"} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-sky-200 to-indigo-300 text-white text-3xl font-bold">
            {(title || "P")[0].toUpperCase()}
          </div>
        )}
        {editable && (
          <button
            className="absolute bottom-2 right-2 bg-white/80 text-xs px-2 py-1 rounded-md shadow hover:bg-white"
            onClick={() => document.getElementById("fileInput").click()}
          >
            Change Image
          </button>
        )}
        {editable && (
          <input
            type="file"
            id="fileInput"
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files[0]) {
                onEdit({ _imageFile: e.target.files[0], image: URL.createObjectURL(e.target.files[0]) });
              }
            }}
          />
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Title + one-liner */}
        <div>
          {editable ? (
            <>
              <input
                className="w-full text-lg font-semibold border-b border-gray-200 focus:outline-none focus:border-blue-500"
                value={title || ""}
                placeholder="Untitled Project"
                onChange={(e) => onEdit({ title: e.target.value })}
              />
              <input
                className="w-full mt-1 text-sm text-gray-600 border-b border-gray-200 focus:outline-none focus:border-blue-500"
                value={oneLiner || ""}
                placeholder="One-liner pitch"
                onChange={(e) => onEdit({ oneLiner: e.target.value })}
              />
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-900">{title || "Untitled Project"}</h2>
              {oneLiner && <p className="text-sm text-gray-600 mt-0.5">{oneLiner}</p>}
            </>
          )}
        </div>

        {/* Description */}
        {editable ? (
          <textarea
            className="w-full text-sm text-gray-700 border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Describe your project…"
            value={description || ""}
            onChange={(e) => onEdit({ description: e.target.value })}
          />
        ) : (
          description && <p className="text-sm text-gray-700">{description}</p>
        )}

        {/* Roles */}
        {openRoles?.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase mb-1">Open Roles</div>
            <div className="flex flex-col gap-2">
              {openRoles.map((role, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-lg border border-gray-200 bg-neutral-50 p-2 text-sm"
                >
                  <BriefcaseIcon className="h-4 w-4 text-gray-700 mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900">{role.roleTitle}</div>
                    {editable ? (
                      <textarea
                        className="w-full text-xs text-gray-600 border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Role description…"
                        value={role.description || ""}
                        onChange={(e) => {
                          const updated = [...openRoles];
                          updated[i].description = e.target.value;
                          onEdit({ openRoles: updated });
                        }}
                      />
                    ) : (
                      role.description && <p className="text-gray-600 text-xs">{role.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectPreviewCard;
